import type { ItineraryRouteDay } from '../api/directions.api';

export interface User {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
  twoFactorEnabled: boolean;
  emailVerifiedAt: string | null;
  termsAcceptedAt: string | null;
  tripCredits: number;
  aiCredits: number;
  premiumAiCredits: number;
  /** ADR-0023: decided by the server from `proUntil`; the client only paints it. */
  isPro: boolean;
  proUntil: string | null;
  proSource: 'STRIPE' | 'APP_STORE' | 'PLAY_STORE' | 'GRANT' | null;
  nationalityCode: string | null;
  /** ADR-0029: `YYYY-MM-DD`, their own — the trip only ever sees the age. */
  birthDate: string | null;
  /** False for Google-only accounts, which have no password to change yet. */
  hasPassword: boolean;
  createdAt: string;
}

export interface TripParticipant {
  id: string;
  tripId: string;
  userId: string;
  role: 'VIEWER' | 'EDITOR' | 'OWNER';
  joinedAt: string;
  // isPlaceholder: a traveller without an account (ADR-0021). Absent on
  // payloads that don't select it, which only ever list real accounts.
  user: Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'> & { isPlaceholder?: boolean };
  /** ADR-0029: age on the trip's first day, worked out by the server. */
  ageAtTripStart?: number | null;
  /** Only present where the viewer may read it: their own, or a placeholder's for editors. */
  birthDate?: string;
}

export interface FlightDocument {
  id: string;
  flightId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export interface FlightParticipant {
  id: string;
  flightId: string;
  userId: string;
  user: Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'>;
}

export interface FlightStop {
  id: string;
  flightId: string;
  fromAirport: string;
  toAirport: string;
  departureTime: string;
  arrivalTime: string;
  flightNumber: string | null;
  airline: string | null;
  reminderMinutes: number | null;
  sortOrder: number;
}

export interface Flight {
  id: string;
  tripId: string;
  direction: 'OUTBOUND' | 'RETURN';
  flightNumber: string | null;
  airline: string | null;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
  bookingRef: string | null;
  notes: string | null;
  source: ContentSource;
  documents?: FlightDocument[];
  stops?: FlightStop[];
  participants?: FlightParticipant[];
}

export interface HotelDocument {
  id: string;
  hotelId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export interface Hotel {
  id: string;
  tripId: string;
  name: string;
  address: string | null;
  checkIn: string;
  checkOut: string;
  bookingRef: string | null;
  bookingUrl: string | null;
  reminderMinutes: number | null;
  notes: string | null;
  source: ContentSource;
  documents?: HotelDocument[];
}

/** ADR-0030: one leg of the trip. Its dates are derived from the nights. */
export interface TripDestination {
  /** Null only for the synthetic leg of a trip that has no rows. */
  id: string | null;
  name: string;
  latitude: number | null;
  longitude: number | null;
  countryCode: string | null;
  country: string | null;
  nights: number;
  sortOrder: number;
}

export interface Trip {
  id: string;
  ownerId: string;
  name: string;
  /** The joined names of `destinations` — display only. */
  destination: string;
  destinationLat: number | null;
  destinationLng: number | null;
  /** In `get`/`update`; absent in the trip list. */
  destinations?: TripDestination[];
  startDate: string;
  endDate: string;
  nights: number;
  coverImageUrl: string | null;
  coverImagePhotographer: string | null;
  coverImagePhotographerUrl: string | null;
  notes: string | null;
  budget: number | null;
  budgetCurrency: string;
  /** Language the AI writes trip content in — shared by all participants. */
  contentLanguage: string;
  shareToken: string | null;
  /** ADR-0012: which sections the public share link may show. */
  shareSections: TripShareSection[];
  isPublished: boolean;
  publishedAt: string | null;
  cloneCount: number;
  isDemo: boolean;
  createdAt: string;
  updatedAt: string;
  owner: Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'>;
  participants: TripParticipant[];
  flights?: Flight[];
  hotels?: Hotel[];
  /**
   * ADR-0033: the role the server applies to you. Never derive it from
   * `ownerId`: while an agency runs the trip its owner acts as an editor, and
   * the agency's staff — who are no participant — as the owner.
   */
  viewerRole: TripParticipant['role'];
  /** You are agency staff running this trip. Only in `get`. */
  viewerIsAgency?: boolean;
  agencyId: string | null;
  isTemplate: boolean;
  /** The agency's card, only while it runs the trip. */
  agency: AgencyBrand | null;
}

export interface AgencyBrand {
  id: string;
  name: string;
  logoUrl: string | null;
  emergencyPhone: string | null;
  contactEmail: string | null;
}

export interface CommunityTripSummary {
  id: string;
  name: string;
  destination: string;
  destinationLat: number | null;
  destinationLng: number | null;
  nights: number;
  startDate: string;
  endDate: string;
  coverImageUrl: string | null;
  coverImagePhotographer: string | null;
  coverImagePhotographerUrl: string | null;
  publishedAt: string;
  cloneCount: number;
  avgRating: number | null;
  ratingCount: number;
  myRating: number | null;
  owner: Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'>;
  activityCount: number;
  participantCount: number;
  expenseTotal: number | null;
  expenseCurrency: string;
}

export interface CommunityTripDetail extends CommunityTripSummary {
  notes: string | null;
  budget: number | null;
  budgetCurrency: string;
  days: ItineraryDay[];
  venues: Venue[];
  tips: Tip[];
  expenseSummary: { total: number; byCategory: Record<string, number>; currency: string } | null;
  isFollowing: boolean;
}

export type ActivityType =
  | 'RESTAURANT' | 'CAFE' | 'MONUMENT' | 'VIEWPOINT' | 'NEIGHBORHOOD'
  | 'PARK' | 'SHOP' | 'EXPERIENCE' | 'TRANSPORT' | 'PARKING' | 'BEACH' | 'NATURE' | 'ACCOMMODATION' | 'OTHER';

// Provenance of trip content. IMPORT = from the user's uploaded document (the
// fixed base the AI must not reorganize), AI = accepted assistant proposal,
// MANUAL = hand-entered. See server ContentSource enum.
export type ContentSource = 'MANUAL' | 'IMPORT' | 'AI';

export interface Activity {
  id: string;
  dayId: string;
  title: string;
  type: ActivityType;
  /** ADR-0020: the user's own type for an OTHER stop; null for every other type. */
  customType?: string | null;
  startTime: string | null;
  durationMins: number | null;
  venueName: string | null;
  address: string | null;
  zone: string | null;
  bookingUrl: string | null;
  notes: string | null;
  sortOrder: number;
  travelMode: string | null;
  placeId: string | null;
  venueId: string | null;
  latitude: number | null;
  longitude: number | null;
  isOrigin: boolean;
  // Skippable stop — stays in the day and in the route, just flagged so a day
  // that runs late has an obvious candidate to drop.
  isOptional: boolean;
  /** ADR-0010: a fixed hour the day's schedule works around instead of over. */
  startTimeLocked: boolean;
  source: ContentSource;
  // Set when the card was generated from a booking rather than typed by the
  // user — a stay (one card per night), a flight, or any other ticket. At most
  // one is ever set; the booking owns the card and keeps it in sync.
  hotelId: string | null;
  flightId: string | null;
  bookingId: string | null;
  /** ADR-0024: the meal this stop is. Deleting a linked stop asks about the meal. */
  mealId: string | null;
  /** ADR-0039: the travellers' own stop as agency staff see it — only its slot. */
  maskedForAgency?: boolean;
}

export interface ItineraryDay {
  id: string;
  tripId: string;
  dayNumber: number;
  date: string;
  title: string | null;
  notes: string | null;
  startFromPrevStop: boolean;
  /** ADR-0010: the hour this day's schedule cascades from. Null = hours by hand. */
  startTime: string | null;
  activities: Activity[];
}

export type VenueCategory =
  | 'RESTAURANT' | 'CAFE' | 'ATTRACTION' | 'VIEWPOINT' | 'NEIGHBORHOOD'
  | 'PARK' | 'SHOP' | 'EXPERIENCE' | 'TRANSPORT_HUB'
  | 'BEACH' | 'NATURE' | 'PARKING' | 'OTHER';

export interface Venue {
  id: string;
  tripId: string;
  name: string;
  category: VenueCategory;
  /** ADR-0019: the user's own name for an OTHER place; null for every other category. */
  customCategory: string | null;
  zone: string | null;
  address: string | null;
  bookingUrl: string | null;
  mapsUrl: string | null;
  placeId: string | null;
  latitude: number | null;
  longitude: number | null;
  priceInfo: string | null;
  notes: string | null;
  visited: boolean;
  sortOrder: number;
  source: ContentSource;
  createdAt: string;
  /**
   * Only sent by the list endpoint: the Google details (photos/rating/hours) are
   * already cached server-side, so the venue card can render them for free.
   * When false the card stays lean until the user asks for them.
   */
  hasCachedDetails?: boolean;
}

export type ExpenseCategory = 'ACCOMMODATION' | 'FLIGHTS' | 'FOOD' | 'ACTIVITIES' | 'TRANSPORT' | 'FUEL' | 'PARKING' | 'CASH' | 'OTHER';
export type PaymentMethod = 'CASH' | 'CARD' | 'TRANSFER';
export type ExpenseStatus = 'PAID' | 'PENDING';

export interface ExpenseSplit {
  id: string;
  expenseId: string;
  userId: string;
  amount: number;
}

export interface Expense {
  id: string;
  tripId: string;
  payerId: string;
  description: string;
  category: ExpenseCategory;
  amount: number;
  currency: string;
  // Set when the expense was entered in a currency other than the trip's base
  // currency: amount/currency are the converted (base) value, these keep the
  // original input and the rate applied (1 originalCurrency = exchangeRate base).
  originalAmount?: number | null;
  originalCurrency?: string | null;
  exchangeRate?: number | null;
  paymentMethod: PaymentMethod;
  status: ExpenseStatus;
  date: string;
  notes: string | null;
  source: ContentSource;
  payer: Pick<User, 'id' | 'username' | 'displayName'>;
  splits: ExpenseSplit[];
  // The reservation (ticket) this expense paid for. At most one is set; read
  // them through `expenseReservation()` rather than one by one.
  flightId?: string | null;
  hotelId?: string | null;
  bookingId?: string | null;
}

export type ReservationType = 'FLIGHT' | 'HOTEL' | 'BOOKING';

/** How the API takes an expense's reservation: one tagged id, never three. */
export interface ReservationLink {
  type: ReservationType;
  id: string;
}

export interface ExpenseDebt {
  id: string;
  tripId: string;
  debtorId: string;
  creditorId: string;
  amount: number;
  currency: string;
  settled: boolean;
  settledAt: string | null;
  debtor: Pick<User, 'id' | 'username' | 'displayName'>;
  creditor: Pick<User, 'id' | 'username' | 'displayName'>;
}

export interface ExpenseSummary {
  total: number;
  byCategory: Record<string, number>;
  byPayer: Record<string, number>;
  // Split-share per participant (what each person consumes), distinct from
  // byPayer (what they laid out) — the per-person budget cap reads this.
  byParticipant: Record<string, number>;
  byDay: Record<string, number>;
  count: number;
}

export type BudgetScope = 'TOTAL' | 'DAILY' | 'CATEGORY' | 'PERSON';
export type BudgetState = 'ok' | 'warn' | 'over';

export interface BudgetLimit {
  id: string;
  tripId: string;
  scope: BudgetScope;
  category: ExpenseCategory | null;
  userId: string | null;
  amount: number;
}

export interface BudgetStatusItem {
  id: string;
  scope: BudgetScope;
  category: ExpenseCategory | null;
  userId: string | null;
  limit: number;
  used: number;
  pct: number;
  state: BudgetState;
  overDays?: string[];
  dailyBreakdown?: { date: string; used: number }[];
}

export interface RedistributionPreview {
  expenseCount: number;
  totalAmount: number;
  newcomers: { id: string; name: string }[];
}

export interface BudgetsResponse {
  limits: BudgetLimit[];
  status: BudgetStatusItem[];
}

// What the client sends to replace the trip's budget set (no ids).
export interface BudgetLimitInput {
  scope: BudgetScope;
  category?: ExpenseCategory | null;
  userId?: string | null;
  amount: number;
}

export interface ChecklistItem {
  id: string;
  sectionId: string;
  text: string;
  checked: boolean;
  sortOrder: number;
  /** Who brings it, on a shared section. Responsibility, not visibility. */
  assignedToUserId: string | null;
}

export interface ChecklistSection {
  id: string;
  tripId: string;
  title: string;
  sortOrder: number;
  source: ContentSource;
  /** null = a shared section of the trip; otherwise whose suitcase it is. */
  ownerUserId: string | null;
  items: ChecklistItem[];
}

/** What the client asks for; the server derives the owner from the caller. */
export type PackingScope = 'personal' | 'common';

/**
 * ADR-0017: one piece of the owner's own suitcase planned for a day. It points
 * at the item and never copies it, so its text and "packed?" come from the
 * suitcase.
 */
export interface OutfitPiece {
  id: string;
  dayId: string;
  ownerUserId: string;
  checklistItemId: string;
  /** Optional, user-named grouping. null = straight on the day. */
  groupId: string | null;
  sortOrder: number;
  item: { text: string; checked: boolean };
}

export interface OutfitGroup {
  id: string;
  dayId: string;
  ownerUserId: string;
  title: string;
  sortOrder: number;
}

/** What a copy-from-another-trip run actually wrote. */
export interface PackingCopyResult {
  /** Sections created or grown, so the tab merges them without refetching. */
  sections: ChecklistSection[];
  addedSections: number;
  addedItems: number;
}

/**
 * ADR-0008: a meal is its own thing, not an itinerary stop. Most meals on a
 * self-catering trip never happen at a restaurant, and a stop has a start time,
 * not a slot.
 */
export type MealSlot = 'BREAKFAST' | 'LUNCH' | 'SNACK' | 'DINNER' | 'OTHER';

/** In the order of the day. `OTHER` last, because it has no hour of its own. */
export const MEAL_SLOTS: MealSlot[] = ['BREAKFAST', 'LUNCH', 'SNACK', 'DINNER', 'OTHER'];

export interface Meal {
  id: string;
  dayId: string;
  slot: MealSlot;
  title: string;
  notes: string | null;
  /** Optional: a place already saved in this trip. */
  venueId: string | null;
  venue: { id: string; name: string; category: VenueCategory; placeId: string | null } | null;
  /** ADR-0024: the itinerary stop this meal is; title and place are kept equal on both. */
  activity: { id: string; dayId: string; startTime: string | null } | null;
  sortOrder: number;
  ingredients: MealIngredient[];
}

/** ADR-0014: base units only — kg and l are converted by the server. */
export type PantryUnit = 'UNIT' | 'GRAM' | 'MILLILITRE';

/** What the user may type; the server converts it to the product's base unit. */
export type InputUnit = 'u' | 'g' | 'kg' | 'ml' | 'l';
// Units first: most things are bought and eaten by the piece (ADR-0016).
export const INPUT_UNITS: InputUnit[] = ['u', 'g', 'kg', 'ml', 'l'];

export interface MealIngredient {
  id: string;
  amount: number;
  /** Formatted by the server ("400 g", "1,5 l"). */
  label: string;
  pantryItem: { id: string; name: string; unit: PantryUnit };
}

/** One counted product, computed and formatted by the server (ADR-0014). */
export interface PantryRow {
  id: string;
  name: string;
  unit: PantryUnit;
  bought: number;
  pending: number;
  planned: number;
  remainingNow: number;
  missing: number;
  labels: { bought: string; pending: string; planned: string; remainingNow: string; missing: string };
}

/** The trip's food shopping list. Hangs off the trip: you shop once for days. */
export interface GroceryItem {
  id: string;
  text: string;
  quantity: string | null;
  bought: boolean;
  /** Only a label ("this is for day 3's dinner"). Cleared, not deleted, with the meal. */
  mealId: string | null;
  /** Set on a counted line; its `quantity` is then the server-formatted amount. */
  pantryItemId: string | null;
  /** Base amount and unit of a counted line; null on a free line from before ADR-0016. */
  amount: number | null;
  unit: PantryUnit | null;
  sortOrder: number;
}

export type BookingType = 'TRANSPORT' | 'EVENT' | 'ACTIVITY' | 'RESTAURANT' | 'OTHER';

export interface BookingDocument {
  id: string;
  bookingId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export interface Booking {
  id: string;
  tripId: string;
  type: BookingType;
  title: string;
  date: string;
  /** Set only when the booking spans days (car rental, multi-day pass). */
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  venue: string | null;
  address: string | null;
  bookingRef: string | null;
  bookingUrl: string | null;
  reminderMinutes: number | null;
  notes: string | null;
  source: ContentSource;
  createdAt: string;
  documents: BookingDocument[];
}

export interface TripPhoto {
  id: string;
  tripId: string;
  dayId: string | null;
  filename: string;
  thumbnailFilename: string | null;
  originalName: string;
  mimeType: string;
  size: number;
  width: number | null;
  height: number | null;
  /** Camera wall-clock time ("…Z" but not UTC): format its date part only. */
  takenAt: string | null;
  activityId: string | null;
  activity: { id: string; title: string } | null;
  /** ADR-0043: the diary entry that chose it, if any. */
  journalEntryId: string | null;
  createdAt: string;
  uploadedBy: Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'>;
}

/** ADR-0043: what the group lived on a day, with the photos its author chose. */
export interface JournalEntry {
  id: string;
  tripId: string;
  dayId: string;
  body: string;
  /** ADR-0044: whether followers of the link see it — the author's call. */
  shared: boolean;
  createdAt: string;
  updatedAt: string;
  author: Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'>;
  photoIds: string[];
}

export interface Tip {
  id: string;
  tripId: string;
  category: string;
  content: string;
  sortOrder: number;
  source: ContentSource;
}

export interface NoteDocument {
  id: string;
  noteId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  createdAt: string;
}

export interface TripNote {
  id: string;
  tripId: string;
  title: string;
  content: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  documents: NoteDocument[];
}

export interface TripExportData {
  trip: Trip & {
    owner: Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'>;
    participants: TripParticipant[];
  };
  days: ItineraryDay[];
  venues: Venue[];
  flights: Flight[];
  hotels: Hotel[];
  bookings: Booking[];
  expenses: Expense[];
  debts: ExpenseDebt[];
  checklists: ChecklistSection[];
  tips: Tip[];
  /** ADR-0008: shared trip content, so every member's export carries the same. */
  meals: PdfMeal[];
  grocery: PdfGroceryItem[];
}

/** The export's narrower shape: the PDF needs a name, not the whole relation. */
export interface PdfMeal {
  id: string;
  dayId: string;
  slot: MealSlot;
  title: string;
  notes: string | null;
  venue: { name: string } | null;
}

export interface PdfGroceryItem {
  id: string;
  text: string;
  quantity: string | null;
  bought: boolean;
}

// ─── PUBLIC SHARE LINK ────────────────────────────────────────────────────────
// ADR-0012. These types are deliberately NOT `Pick<Trip, …>` of the private
// ones: the shared payload is a different, smaller object, and typing it as a
// subset of the real trip is how a field nobody decided on gets rendered the day
// the server starts sending it. What the server is allowed to send is decided in
// `server/src/modules/trips/sharedTrip.service.ts`.

export const TRIP_SHARE_SECTIONS = [
  'ITINERARY', 'NOTES', 'PLACES', 'MAP', 'MEALS', 'TIPS', 'PHOTOS', 'BOOKINGS', 'DIARY',
] as const;
export type TripShareSection = typeof TRIP_SHARE_SECTIONS[number];

/**
 * What a link created before the sections existed was backfilled to (migration
 * 20260923120000). Recognising exactly this set is the only honest way to tell
 * "nobody has chosen yet" from "the owner deliberately switched things off".
 */
export const TRIP_SHARE_LEGACY_SECTIONS: TripShareSection[] = ['ITINERARY', 'BOOKINGS'];

export interface SharedActivity {
  id: string;
  title: string;
  type: ActivityType;
  startTime: string | null;
  durationMins: number | null;
  venueName: string | null;
  address: string | null;
  zone: string | null;
  sortOrder: number;
  isOptional: boolean;
  isOrigin: boolean;
  /** Only when NOTES is on. */
  notes?: string | null;
  /** Only when MAP is on. */
  latitude?: number | null;
  longitude?: number | null;
}

export interface SharedDay {
  id: string;
  dayNumber: number;
  date: string;
  title: string | null;
  startTime: string | null;
  activities: SharedActivity[];
}

export interface SharedVenue {
  id: string;
  name: string;
  type: ActivityType;
  category: VenueCategory;
  zone: string | null;
  address: string | null;
  priceInfo: string | null;
  mapsUrl: string | null;
  placeId: string | null;
  latitude: number | null;
  longitude: number | null;
  sortOrder: number;
  notes?: string | null;
}

export interface SharedMeal {
  id: string;
  dayId: string;
  slot: MealSlot;
  title: string;
  venue: { name: string } | null;
  notes?: string | null;
}

export interface SharedTip {
  id: string;
  category: string;
  content: string;
  sortOrder: number;
}

export interface SharedPhoto {
  id: string;
  dayId: string | null;
  filename: string;
  thumbnailFilename: string | null;
  width: number | null;
  height: number | null;
}

export interface SharedFlight {
  id: string;
  direction: 'OUTBOUND' | 'RETURN';
  flightNumber: string | null;
  airline: string | null;
  departureAirport: string;
  arrivalAirport: string;
  departureTime: string;
  arrivalTime: string;
  notes?: string | null;
}

export interface SharedHotel {
  id: string;
  name: string;
  address: string | null;
  checkIn: string;
  checkOut: string;
  bookingUrl: string | null;
  notes?: string | null;
}

export interface SharedBooking {
  id: string;
  type: BookingType;
  title: string;
  date: string;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  venue: string | null;
  address: string | null;
  bookingUrl: string | null;
  notes?: string | null;
}

export interface SharedTrip {
  id: string;
  name: string;
  destination: string;
  destinationLat: number | null;
  destinationLng: number | null;
  destinationCountry: string | null;
  startDate: string;
  endDate: string;
  nights: number;
  coverImageUrl: string | null;
  coverImagePhotographer: string | null;
  coverImagePhotographerUrl: string | null;
  notes: string | null;
  owner: Pick<User, 'username' | 'displayName' | 'avatarUrl'>;
  /** A head count, never the travellers: a public page is not a roster. */
  memberCount: number;
  sections: TripShareSection[];
  days?: SharedDay[];
  venues?: SharedVenue[];
  meals?: SharedMeal[];
  tips?: SharedTip[];
  photos?: SharedPhoto[];
  flights?: SharedFlight[];
  hotels?: SharedHotel[];
  bookings?: SharedBooking[];
  route?: ItineraryRouteDay[];
  diary?: SharedDiaryEntry[];
  /** ADR-0045: with the diary — counts only. */
  summary?: TripSummary;
}

/** ADR-0044: an entry its author left shared; of the author, only the name. */
export interface SharedDiaryEntry {
  id: string;
  dayId: string;
  day: { dayNumber: number; date: string };
  body: string;
  createdAt: string;
  authorName: string;
  photos: SharedPhoto[];
}

/** ADR-0044: a trip followed through its link. */
export interface FollowedTrip {
  shareToken: string;
  name: string;
  destination: string;
  startDate: string;
  endDate: string;
  coverImageUrl: string | null;
}

// ── ADR-0045: the trip in numbers and the traveller's stats ──────────────────

export interface TripSummary {
  days: number;
  stops: number;
  countries: string[];
  /** Null when not the viewer's to know (agency staff). */
  photos: number | null;
  entries: number | null;
  /** Null unless every leg of the trip is in the route cache. */
  km: number | null;
}

export interface TravelStats {
  trips: number;
  nights: number;
  countries: { code: string; flag: string | null; region: string | null }[];
  regions: string[];
}

/** Paths already projected by the server; `countries` is keyed by ISO-2. */
export interface WorldMapData {
  width: number;
  height: number;
  countries: Record<string, string>;
}

// ── ADR-0046: the traveller's own album of the trip ──────────────────────────

export type AlbumTemplate =
  | 'COVER' | 'STATS' | 'ONE_PHOTO' | 'TWO_PHOTOS' | 'THREE_PHOTOS' | 'FOUR_PHOTOS' | 'TEXT_PHOTO' | 'TEXT_ONLY'
  | 'TRIP_MAP' | 'DAY_MAP' | 'FULL_BLEED' | 'TWO_PHOTOS_ASYM' | 'THREE_STRIPS' | 'FIVE_PHOTOS' | 'SIX_PHOTOS'
  | 'MOSAIC' | 'TWO_PHOTOS_TEXT' | 'QUOTE' | 'PANORAMA' | 'PANORAMA_FRAMED' | 'TRIP_MAP_SPREAD' | 'COVER_WRAP'
  | 'COVER_FULL' | 'COVER_TYPE' | 'COVER_FRAMED' | 'COVER_MOSAIC' | 'COVER_MAP' | 'COVER_WRAP_FRAMED' | 'COVER_WRAP_TWO';

/** ADR-0052: where a template goes — one page, across an inner spread, or across the cover. */
export type AlbumTemplatePlace = 'page' | 'spread' | 'cover';

export type AlbumSlotKind = 'PHOTO' | 'TITLE' | 'TEXT' | 'MAP';

/** A slot's frame in mm on the page — defined by the server, never here. */
export interface AlbumSlotFrame {
  kind: AlbumSlotKind;
  x: number;
  y: number;
  w: number;
  h: number;
  maxChars?: number;
  /** Text slots: the alignment until the traveller picks one. */
  align?: 'center';
  /** A title over a photo: light ink on a dark fade, whatever the page's ink. */
  onPhoto?: true;
  /** Text slots: the size in pt the frame is laid out at; absent = `fontPt` of its kind. */
  pt?: number;
}

export interface AlbumMapStyle {
  sea: string;
  land: string;
  coast: string;
  paper: string;
  ink: string;
  tones: string[];
}

export interface AlbumFormat {
  id: string;
  width: number;
  height: number;
  family: 'portrait' | 'landscape' | 'square';
}

export interface AlbumCatalogue {
  /** The format this catalogue's page and frames are cut to (ADR-0049). */
  format: string;
  formats: AlbumFormat[];
  page: { width: number; height: number };
  fontPt: Record<'TITLE' | 'TEXT', number>;
  mapStyle: AlbumMapStyle;
  /** Where the figures page draws its map, in mm of the page. */
  figuresMap: { x: number; y: number; w: number; h: number };
  /** ADR-0049: how much the figures page shrinks in this format (1 at the family's size). */
  figuresScale: number;
  /** The page margin in this format, in mm. */
  margin: number;
  /** Spread templates' frames are on two pages side by side; the cover wrap's on two pages and the spine. */
  /** `cover`: laid out as a front cover — offered first on the cover; absent from an older server. */
  templates: { id: AlbumTemplate; slots: AlbumSlotFrame[]; place: AlbumTemplatePlace; cover?: boolean }[];
  /** ADR-0052: the cover's spine, in mm — an estimate from the page count; its title only from `minForText` up. */
  spine: { width: number; minForText: number; fontPt: number; maxChars: number };
}

export interface AlbumSlot {
  index: number;
  photoId: string | null;
  /** ADR-0051: or one of the album's own photos — never both. */
  albumPhotoId: string | null;
  text: string | null;
  /** ADR-0047: the day a DAY_MAP slot draws. */
  dayId: string | null;
  /** Text slots: S/M/L and left/center/right; null = M, left. */
  textSize: AlbumTextSize | null;
  textAlign: AlbumTextAlign | null;
  /** ADR-0053: an `AlbumStyles.fonts` id; null = the album pair's for the box's kind. */
  font: string | null;
  /** ADR-0053: the box's size in pt; null = `textSize`'s step over the frame's size. */
  textPt: number | null;
  /** Photo slots: which part of an overflowing photo shows, 0–1 (object-position %); null = centred. */
  photoX: number | null;
  photoY: number | null;
}

export type AlbumTextSize = 'S' | 'M' | 'L';
export type AlbumTextAlign = 'left' | 'center' | 'right';

export interface AlbumFont {
  family: string;
  weight: 400 | 500 | 600 | 700;
  italic: boolean;
}

/** ADR-0053: a cut a text box can take on its own. */
export interface AlbumFontOption extends AlbumFont {
  id: string;
}

export interface AlbumFontPair {
  id: string;
  title: AlbumFont;
  body: AlbumFont;
}

/** ADR-0047: a map drawn by the server, in mm of its slot. Clients only paint it. */
export interface AlbumMap {
  width: number;
  height: number;
  /** `paper`: a country's outline alone on the page, no sea. */
  background: 'sea' | 'paper';
  land: string;
  routes: { d: string; kind: 'road' | 'approx' | 'transfer'; tone: number }[];
  pins: { x: number; y: number; label: string | null; hasPhoto: boolean; tone: number }[];
  legend: { y: number; h: number; kind: 'stops' | 'days'; items: { n: number; label: string; tone: number }[] } | null;
}

export type AlbumFiguresMap = 'WORLD' | 'COUNTRY';

export type AlbumVeil = 'dark' | 'light';

/** ADR-0052: BOTH is a page across the whole spread — a panorama, or the cover's wrap. */
export type AlbumSide = 'LEFT' | 'RIGHT' | 'BOTH';
export type AlbumSpreadKind = 'COVER' | 'OPENING' | 'INNER' | 'CLOSING';

export interface AlbumPage {
  id: string;
  side: AlbumSide;
  template: AlbumTemplate;
  /** The figures page's map: the world, or the visited countries' outline. */
  figuresMap: AlbumFiguresMap;
  /** The country outline with the trip's route, or with only its stops. */
  figuresRoute: boolean;
  /** Off: the route and stops without the country's outline under them. */
  figuresOutline: boolean;
  /** A map page's legend; off, the map takes its band (its own map key, `:bare`). */
  mapLegend: boolean;
  /** ADR-0048: a background id of the catalogue; null = the theme's. */
  background: string | null;
  /** A trip photo covering the page, under a veil. */
  backgroundPhotoId: string | null;
  backgroundVeil: AlbumVeil;
  /** ADR-0051: or one of the album's own photos — never both. */
  backgroundAlbumPhotoId: string | null;
  slots: AlbumSlot[];
}

/** ADR-0052: two facing pages, or one across both. Only inner spreads move or go. */
export interface AlbumSpread {
  id: string;
  kind: AlbumSpreadKind;
  /** ADR-0050: the day the draft made this spread for; only these can be redone. */
  dayId: string | null;
  pages: AlbumPage[];
}

/** ADR-0050: how many photos a day gets in the draft. */
export type AlbumDensity = 'FEW' | 'NORMAL' | 'ALL';

/** ADR-0051: a photo uploaded for this album only — the group never sees it. */
export interface AlbumOwnPhoto {
  id: string;
  filename: string;
  thumbnailFilename: string;
  originalName: string;
  width: number | null;
  height: number | null;
  createdAt: string;
}

/** What a page paints a photo from: a trip photo or an own one. */
export type AlbumPicture = Pick<TripPhoto, 'id' | 'filename' | 'thumbnailFilename' | 'originalName' | 'width' | 'height'>;

/** ADR-0050: the wizard's choices; all optional. */
export interface AlbumDraftChoices {
  format?: string;
  theme?: string;
  density?: AlbumDensity;
  coverPhotoId?: string | null;
}

export interface AlbumDraftOptions {
  pages: Record<AlbumDensity, number>;
  maxPages: number;
  coverPhotoId: string | null;
}

export interface Album {
  id: string;
  tripId: string;
  /** ADR-0048: a theme id of the catalogue; an unknown one paints as the default. */
  theme: string;
  /** ADR-0049: a format id of the catalogue; the server sends the catalogue already cut to it. */
  format: string;
  /** ADR-0048: a typeface pair id; unknown ones paint as the default. */
  fontPair: string;
  photoFrame: string;
  density: AlbumDensity;
  /** ADR-0052: the title on the cover's spine. */
  spineText: string | null;
  updatedAt: string;
  photos: AlbumOwnPhoto[];
  spreads: AlbumSpread[];
}

export type AlbumInkId = 'dark' | 'light';

export interface AlbumInk {
  text: string;
  muted: string;
}

export interface AlbumBackground {
  id: string;
  color: string;
  ink: AlbumInkId;
  pattern?: { kind: 'dots' | 'stripes' | 'grid' | 'notebook'; color: string; spacing: number; size: number };
}

export interface AlbumTheme {
  id: string;
  background: string;
  accent: string;
  map: AlbumMapStyle;
}

/** ADR-0048: every look the album can take, from the server. */
export interface AlbumStyles {
  themes: AlbumTheme[];
  backgrounds: AlbumBackground[];
  inks: Record<AlbumInkId, AlbumInk>;
  defaultTheme: string;
  fontPairs: AlbumFontPair[];
  /** ADR-0053: every cut a text box can take, the pairs' included. */
  fonts: AlbumFontOption[];
  defaultFontPair: string;
  photoFrames: string[];
  textSizeScale: Record<AlbumTextSize, number>;
  /** ADR-0053: the sizes a box can take, in pt, by kind. */
  textPt: Record<'TITLE' | 'TEXT', { min: number; max: number }>;
}

export interface AlbumView {
  album: Album | null;
  catalogue: AlbumCatalogue;
  styles: AlbumStyles;
  summary: TripSummary | null;
  /** Keyed by `albumMapKey` (`utils/albumMaps.ts`). */
  maps: Record<string, AlbumMap>;
  /** What the figures page heads with; null until there is an album. */
  trip: { name: string; destination: string; startDate: string; endDate: string } | null;
}
